import React, { useMemo, useState } from 'react';
import { Box, Button, Collapse, Stack, Typography } from '@mui/material';
import {
  ChevronLeft, ChevronRight, ExpandLess, ExpandMore, Today,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import Panel from '../crt/Panel';
import ShortsPreview from './ShortsPreview';
import { shortsAPI } from '../../services/api';
import { isOverdue, troubles } from './shortsTrouble';

/**
 * Shorts release calendar.
 *
 * Visually matches `ReleaseCalendar` (same CRT tokens, month nav, legend) but
 * is a separate component on purpose: that one renders a FLAT list of events
 * and is shared by five other dashboards, whereas this needs a two-level
 * clip → platform hierarchy. Changing the shared one to suit this page would
 * put the other five at risk for no benefit to them.
 *
 * A day cell lists RELEASES (one per clip), coloured by how the clip as a whole
 * is doing. Expanding a release shows a chip per platform with its own status —
 * because "the clip" doesn't have one status: it can be live on YouTube, queued
 * on Reddit and skipped on Facebook at the same time.
 */

type Target = {
  target_id: string; platform: string; status: string;
  scheduled_at: string; platform_url: string | null;
  native_schedule: boolean; note: string;
  clip_id: string; title: string; ordinal: number;
  batch_id: string; batch_label: string; site: string;
  episode_key: string | null;
  finished_at: string | null; failures: number;
  last_failure: string | null; missed_slot_at: string | null;
};

const COLORS: Record<string, string> = {
  published: '#00ff41',   // fully out, on time, first try
  late:      '#ff9f1a',   // out, but late or only after failed attempts
  overdue:   '#ff5577',   // its slot has passed and it still isn't out
  partial:   '#86d943',   // some platforms out, others still to go
  scheduled: '#ffb000',   // handed over; the platform holds the timer
  uploading: '#c774ff',   // in flight now
  pending:   '#00ffff',   // ours to publish when the slot arrives
  error:     '#ff3344',
  skipped:   '#6b7280',   // deliberately not going
};

const LEGEND: Array<{ key: string; label: string }> = [
  { key: 'published', label: 'RELEASED' },
  { key: 'late',      label: 'LATE / RETRIED' },
  { key: 'overdue',   label: 'OVERDUE' },
  { key: 'partial',   label: 'PARTLY OUT' },
  { key: 'scheduled', label: 'SCHEDULED' },
  { key: 'uploading', label: 'UPLOADING' },
  { key: 'pending',   label: 'QUEUED' },
  { key: 'error',     label: 'ERROR' },
];

const CELL_MIN_HEIGHT = 118;

/** One target's colour key: its status, unless that hides a problem. */
function targetKey(t: Target): string {
  if (isOverdue(t)) return 'overdue';
  if (t.status === 'published' && troubles(t).length) return 'late';
  return t.status;
}

/** How a whole clip is doing, from its per-platform targets. */
function rollUp(targets: Target[]): string {
  const live = targets.filter((t) => t.status !== 'skipped');
  if (targets.some((t) => t.status === 'error')) return 'error';
  if (live.some(isOverdue)) return 'overdue';
  if (live.length === 0) return 'skipped';
  if (live.every((t) => t.status === 'published')) {
    return live.some((t) => targetKey(t) === 'late') ? 'late' : 'published';
  }
  if (live.some((t) => t.status === 'published')) return 'partial';
  if (live.some((t) => t.status === 'uploading')) return 'uploading';
  if (live.some((t) => t.status === 'scheduled')) return 'scheduled';
  return 'pending';
}

const isoDate = (d: Date) => d.toLocaleDateString('sv').slice(0, 10);
const addMonths = (d: Date, n: number) => {
  const x = new Date(d); x.setMonth(x.getMonth() + n); return x;
};
const monthLabel = (d: Date) =>
  d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase();

const ShortsCalendar: React.FC = () => {
  const [open, setOpen] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [previewClip, setPreviewClip] = useState<string | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [monthStart, setMonthStart] = useState(() => {
    const t = new Date(); return new Date(t.getFullYear(), t.getMonth(), 1);
  });

  const { data, isLoading } = useQuery({
    queryKey: ['shorts', 'calendar'],
    queryFn: () => shortsAPI.getCalendar(120, 180),
    refetchInterval: 60_000,
  });

  // The calendar already holds a row per (clip x platform); reuse them so a
  // published clip's preview can link straight out to the live post instead
  // of only offering the staged file.
  const previewTargets = useMemo(
    () => (previewClip
      ? (data?.targets ?? []).filter((t) => t.clip_id === previewClip)
      : []),
    [previewClip, data]);

  /** day -> clip_id -> { clip info, targets } */
  const byDay = useMemo(() => {
    const out: Record<string, Record<string, { t: Target[]; }>> = {};
    (data?.targets ?? []).forEach((t) => {
      // Local day, so a 09:00 PT release doesn't land on the next UTC date.
      const day = isoDate(new Date(t.scheduled_at));
      out[day] = out[day] || {};
      out[day][t.clip_id] = out[day][t.clip_id] || { t: [] };
      out[day][t.clip_id].t.push(t);
    });
    return out;
  }, [data]);

  const days = useMemo(() => {
    const first = new Date(monthStart);
    const start = new Date(first);
    start.setDate(1 - first.getDay());
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start); d.setDate(start.getDate() + i); return d;
    });
  }, [monthStart]);

  const todayIso = isoDate(new Date());
  const toggle = (id: string, set: Set<string>, fn: (s: Set<string>) => void) => {
    const n = new Set(set); n.has(id) ? n.delete(id) : n.add(id); fn(n);
  };

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    Object.values(byDay).forEach((clips) =>
      Object.values(clips).forEach((c2) => {
        const s = rollUp(c2.t); c[s] = (c[s] ?? 0) + 1;
      }));
    return c;
  }, [byDay]);

  const summary = LEGEND.filter((l) => counts[l.key])
    .map((l) => `${counts[l.key]} ${l.label.toLowerCase()}`).join(' · ');

  const releasesThisMonth = useMemo(() => {
    const m = isoDate(monthStart).slice(0, 7);
    return Object.entries(byDay)
      .filter(([d]) => d.startsWith(m))
      .reduce((n, [, clips]) => n + Object.keys(clips).length, 0);
  }, [byDay, monthStart]);

  return (
    <>
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: open ? 1 : 0 }}>
        <Button
          size="small" onClick={() => setOpen((o) => !o)}
          startIcon={open ? <ExpandLess /> : <ExpandMore />}
          sx={{ fontFamily: "'Press Start 2P', monospace", fontSize: 9 }}
        >{open ? 'HIDE' : 'SHOW'} RELEASE CALENDAR</Button>
        <Typography variant="caption" color="text.secondary">
          {isLoading ? 'loading…' : summary || 'nothing scheduled'}
        </Typography>
      </Stack>

      <Collapse in={open} unmountOnExit>
        <Panel label="SHORTS RELEASE CALENDAR">
          {/* Toolbar — same shape as the shared calendar */}
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" mb={2}>
            <Button size="small" variant="outlined" startIcon={<ChevronLeft />}
                    onClick={() => setMonthStart(addMonths(monthStart, -1))}>PREV</Button>
            <Typography variant="h6" sx={{
              minWidth: 220, textAlign: 'center',
              fontFamily: "'Press Start 2P', monospace",
              fontSize: '13px', letterSpacing: '0.08em',
            }}>{monthLabel(monthStart)}</Typography>
            <Button size="small" variant="outlined" endIcon={<ChevronRight />}
                    onClick={() => setMonthStart(addMonths(monthStart, 1))}>NEXT</Button>
            <Button size="small" variant="text" startIcon={<Today />}
                    onClick={() => {
                      const t = new Date();
                      setMonthStart(new Date(t.getFullYear(), t.getMonth(), 1));
                    }}>TODAY</Button>
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" sx={{
              color: '#5aaa5a', fontFamily: "'VT323', monospace", fontSize: '14px' }}>
              {releasesThisMonth} releases this month
            </Typography>
          </Box>

          {/* Legend — click to hide a status */}
          <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
            {LEGEND.map((l) => {
              const off = hidden.has(l.key);
              return (
                <Box key={l.key} onClick={() => toggle(l.key, hidden, setHidden)}
                  sx={{
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5,
                    px: 0.75, py: 0.25, border: '1px solid',
                    borderColor: off ? '#2a4a2a' : COLORS[l.key],
                    opacity: off ? 0.4 : 1,
                    fontFamily: "'VT323', monospace", fontSize: '13px',
                    color: off ? '#2a4a2a' : COLORS[l.key],
                  }}>
                  <Box sx={{ width: 8, height: 8, background: COLORS[l.key] }} />
                  {l.label}
                </Box>
              );
            })}
          </Box>

          {/* Weekday header */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
                     gap: '1px', background: '#1e3a1e', mb: '1px' }}>
            {['SUN','MON','TUE','WED','THU','FRI','SAT'].map((w) => (
              <Box key={w} sx={{
                background: '#0a0a0a', py: 0.75, textAlign: 'center',
                fontFamily: "'Press Start 2P', monospace", fontSize: '9px',
                color: '#5aaa5a', letterSpacing: '0.08em',
              }}>{w}</Box>
            ))}
          </Box>

          {/* Day grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
                     gap: '1px', background: '#1e3a1e' }}>
            {days.map((d, i) => {
              const iso = isoDate(d);
              const inMonth = d.getMonth() === monthStart.getMonth();
              const isToday = iso === todayIso;
              const clips = Object.entries(byDay[iso] || {});
              return (
                <Box key={i} sx={{
                  background: '#0a0a0a', minHeight: CELL_MIN_HEIGHT, p: 0.5,
                  borderLeft: isToday ? '2px solid #00ff41' : 'none',
                  overflow: 'hidden',
                }}>
                  <Typography variant="caption" sx={{
                    display: 'block', fontFamily: "'VT323', monospace", fontSize: '14px',
                    color: isToday ? '#00ff41' : (inMonth ? '#5aaa5a' : '#2a4a2a'),
                    fontWeight: isToday ? 700 : 400, mb: 0.25,
                  }}>{d.getDate()}</Typography>

                  {clips.map(([clipId, { t }]) => {
                    const roll = rollUp(t);
                    if (hidden.has(roll)) return null;
                    const color = COLORS[roll] ?? COLORS.pending;
                    const first = t[0];
                    const ep = first.episode_key ? `${first.episode_key} - ` : '';
                    const isOpen = expanded.has(clipId);
                    return (
                      <Box key={clipId} sx={{
                        mb: '3px', borderLeft: `3px solid ${color}`,
                        background: `color-mix(in srgb, ${color} 12%, transparent)`,
                        filter: inMonth ? 'none' : 'brightness(0.45)',
                      }}>
                        {/* Release row — episode + short title, caret to expand */}
                        <Box
                          onClick={() => toggle(clipId, expanded, setExpanded)}
                          title={`${ep}${first.title} — ${first.batch_label}`}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: '2px',
                            px: '4px', py: '2px', cursor: 'pointer', color,
                            fontFamily: "'VT323', monospace", fontSize: '12px',
                            lineHeight: 1.2,
                            '&:hover': { filter: 'brightness(1.3)' },
                          }}
                        >
                          <Box component="span" sx={{
                            fontSize: '10px', lineHeight: 1,
                            transform: isOpen ? 'rotate(90deg)' : 'none',
                            transition: 'transform 120ms',
                          }}>▸</Box>
                          <Box component="span" sx={{
                            overflow: 'hidden', textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap', flex: 1,
                          }}>{ep}{first.title}</Box>
                          {/* Watch it before it goes out. The calendar is
                              where you look at what is UPCOMING, so this is
                              the place the preview is actually reachable from.
                              stopPropagation so it does not also toggle the
                              row open underneath the dialog. */}
                          <Box
                            component="span"
                            role="button"
                            aria-label={`Preview ${first.title}`}
                            title="Watch this clip"
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              setPreviewClip(clipId);
                            }}
                            sx={{
                              fontSize: '10px', lineHeight: 1, px: '2px',
                              opacity: 0.55,
                              '&:hover': { opacity: 1 },
                            }}
                          >▶</Box>
                        </Box>

                        {/* Which batch this release came from */}
                        <Box sx={{
                          px: '4px', pb: '2px', color: '#5aaa5a',
                          fontFamily: "'VT323', monospace", fontSize: '10px',
                          overflow: 'hidden', textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>{first.batch_label}</Box>

                        {/* Per-platform chips */}
                        <Collapse in={isOpen} unmountOnExit>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '2px',
                                     px: '4px', pb: '3px' }}>
                            {t.slice().sort((a, b) => a.platform.localeCompare(b.platform))
                              .map((x) => {
                              const c = COLORS[targetKey(x)] ?? COLORS.pending;
                              const why = [...troubles(x), x.note]
                                .filter(Boolean).join(' · ');
                              return (
                                <Box
                                  key={x.target_id}
                                  component={x.platform_url ? 'a' : 'span'}
                                  href={x.platform_url ?? undefined}
                                  target={x.platform_url ? '_blank' : undefined}
                                  rel="noreferrer"
                                  title={why
                                    ? `${x.platform}: ${x.status} — ${why}`
                                    : `${x.platform}: ${x.status}`}
                                  sx={{
                                    px: '3px', border: `1px solid ${c}`, color: c,
                                    fontFamily: "'VT323', monospace", fontSize: '11px',
                                    lineHeight: 1.4, textDecoration: 'none',
                                    cursor: x.platform_url ? 'pointer' : 'default',
                                    '&:hover': x.platform_url
                                      ? { filter: 'brightness(1.4)' } : undefined,
                                  }}
                                >{x.platform}</Box>
                              );
                            })}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Box>
        </Panel>
      </Collapse>
    </Box>
    <ShortsPreview
      clipId={previewClip}
      targets={previewTargets as any}
      onClose={() => setPreviewClip(null)}
    />
    </>
  );
};

export default ShortsCalendar;
